<template>
  <AppClientSchoolLayout title="プロフィール変更">
    <template #header>
      <h2 class="flex justify-between text-xl font-semibold leading-tight text-gray-800">
        プロフィール変更
      </h2>
    </template>
    <div class="mx-auto max-w-7xl bg-white m-4 p-8 sm:px-6 lg:px-8">
      <p
        v-if="displayUpdateDoneMessage"
        class="text-red-400 text-xl font-bold text-right">
        {{ displayUpdateDoneMessage }}
      </p>
      <div
        v-if="isError"
        class="flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div
          class="ml-2 text-red-500">
          必須項目を全て入力してください
        </div>
      </div>
      <div class="col-span-9">
        <div class="grid grid-cols-4 text-sm md:text-base text-black py-2 items-center">
          <p>連携用コード</p>
          <p
            type="text"
            class="ml-2 pl-2 text-xs md:text-base col-span-3 sm:col-span-2 md:col-span-2 border-0">
            {{ user.link_code }}
          </p>
        </div>

        <div class="grid grid-cols-4 text-sm md:text-base text-black py-2 items-center">
          <p>学校名<span class="text-red-400 ml-1">* 必須</span></p>
          <input
            v-model="userInfoState.name"
            type="text"
            class="ml-2 pl-2 text-xs md:text-base col-span-3 sm:col-span-2 md:col-span-2 rounded border-blue-400">
        </div>

        <div class="grid grid-cols-4 text-sm md:text-base text-black py-2 items-center">
          <p>郵便番号<span class="text-red-400 ml-1">* 必須</span></p>
          <input
            pattern="[0-9]+"
            maxlength="7"
            :value="userInfoState.postalCode"
            type="text"
            class="ml-2 pl-2 text-xs md:text-base col-span-2 md:col-span-1 rounded border-blue-400"
            @input="userInfoState.postalCode=postalValidate($event)">
          <button
            class="px-4 py-2 ml-2 rounded bg-blue-600 hover:bg-blue-400 text-white max-w-max shadow-sm hover:shadow-lg"
            @click="searchAddress()">
            住所検索
          </button>
        </div>

        <div
          v-if="execSearchFlag && isAddressBlank"
          class="ml-2 text-red-500">
          住所が見つかりません。
        </div>
        <div class="grid grid-cols-4 text-sm md:text-base text-black py-2 items-center">
          <p>住所<span class="text-red-400 ml-1">* 必須</span></p>
          <input
            v-model="userInfoState.address"
            type="text"
            class="ml-2 pl-2 text-xs md:text-base col-span-3 sm:col-span-2 md:col-span-2 rounded border-blue-400">
        </div>

        <div class="grid grid-cols-4 text-sm md:text-base text-black py-2 items-center">
          <p>課程年数（1〜4）<span class="text-red-400 ml-1">* 必須</span></p>
          <input
            v-model="userInfoState.schoolYears"
            type="number"
            min="1"
            max="4"
            class="ml-2 pl-2 text-xs md:text-base col-span-3 sm:col-span-2 md:col-span-2 rounded border-blue-400">
        </div>

        <div class="text-right">
          <Button
            class="ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isBlankForm"
            @click="submit">
            プロフィール情報更新
          </Button>
        </div>
      </div>
    </div>
  </AppClientSchoolLayout>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { usePage } from '@inertiajs/inertia-vue3'
import { Core as YubinBangoCore } from 'yubinbango-core'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import { USER_INFO_AREAS } from '@/Constants/areas'
import { USER_INFO_JOBS } from '@/Constants/jobs'
import { USER_INFO_SERVICE_YEARS } from '@/Constants/serviceYears'
import Button from '@/Jetstream/Button'

const isError = ref(false)
const execSearchFlag = ref(false)

const props = defineProps({
  user: {
    type: Object,
    require: true,
  },
})

const userInfoState = reactive({
  name: props.user.name,
  sex: props.user.sex,
  postalCode: props.user.postal_code,
  address: props.user.address,
  schoolYears: props.user.school_years,
})

const displayUpdateDoneMessage = computed(() => usePage().props.value.flash.message)

const isBlankForm = computed(() => {
  let isBlank = false
  if (!userInfoState.name
      || !userInfoState.postalCode
      || (String(userInfoState.postalCode).length < 7)
      || !userInfoState.address
      || !userInfoState.schoolYears) {
    isBlank = true
  }

  return isBlank
})

const isAddressBlank = computed(() => (!userInfoState.address))

const postalValidate = (e) => {
  const re = new RegExp(e.target.pattern)
  const result = re.exec(e.target.value)
  return e.target.value = result ? result[0] : result
}

const searchAddress = () => {
  new YubinBango.Core(String(userInfoState.postalCode), (addr) => {
    execSearchFlag.value = true
    userInfoState.address = addr.region // 都道府県
    userInfoState.address += addr.locality // 市区町村
    userInfoState.address += addr.street // 町域
  })
}

const submit = () => {
  Inertia.visit(route('school.profile.update'), {
    method: 'post',
    data: {
      userInfoState,
    },
  })
}

</script>
